import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppButton from '@/components/WhatsAppButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';
import { trackViewContent } from '@/lib/metaPixel';

const Login: React.FC = () => {
  const { user, signIn, isLoading } = useSupabaseAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Meta Pixel - ViewContent for Login page
    trackViewContent({
      content_name: 'Login Page',
      content_category: 'Authentication',
    });
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Logged in successfully!');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Login - Access Your TOPPERS BUDDY Account | Class 10 Science Notes</title>
        <meta name="description" content="Login to your TOPPERS BUDDY account to access your purchased class 10 science notes. Quick and easy access to toppers short notes." />
        <meta name="keywords" content="toppers buddy login, student login, access notes, class 10 notes login" />
        <link rel="canonical" href="https://toppersbuddy.lovable.app/login" />
        <meta property="og:title" content="Login - Access Your TOPPERS BUDDY Account" />
        <meta property="og:url" content="https://toppersbuddy.lovable.app/login" />
      </Helmet>

      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main role="main" aria-label="Login" className="flex-1 py-12">
          <div className="container max-w-md mx-auto px-4">
            <Card className="border-border/50 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <LogIn className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
                <CardDescription>Login to access your notes</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Logging in...' : 'Login'}
                  </Button>
                </form>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <a href="/signup" className="text-primary hover:underline">
                    Sign up here
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
      
      <WhatsAppButton />
    </>
  );
};

export default Login;
